import { useState, useEffect, useCallback, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useForm, Controller } from 'react-hook-form';
import {
  Box,
  Typography,
  TextField,
  Button,
  Divider,
  Stack,
  Card,
  CardContent,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormHelperText,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Autocomplete,
  CircularProgress,
  Alert
} from '@mui/material';
import {
  Person,
  Description,
  Build,
  Star,
  Schedule,
  Phone
} from '@mui/icons-material';
import AutoFixHighIcon from '@mui/icons-material/AutoFixHigh';
import { toast } from 'react-toastify';
import { translateTextStream } from '../../services/aiTranslateService';

// Import existing services
import { getSubServiceTypes } from '../../services/subServiceTypeService';
import { getAreas, updateBusinessProfile } from '../../services/profileService';
import { updateStylistDetails, getStylistDetails } from '../../services/stylistService';
import AddressAutocomplete from '../common/AddressAutocomplete';
import AreaSuggestion from '../common/AreaSuggestion';
import type { AcceptSuggestionPayload, AreaSuggestionMatchResult } from '../common/AreaSuggestion';
import { matchPlaceToAreaWithAutoSelect } from '../../utils/areaMatchingUtils';
import type { PlaceData as AreaUtilPlaceData, RegionData as AreaUtilRegionData, MatchWithAutoSelectResult } from '../../utils/areaMatchingUtils';
import type { FormDataValue } from '../../services/api';
import { normalizePhoneNumber, validateHKPhoneNumber, formatPhoneForStorage } from '../../utils/phoneUtils';

interface SubServiceType {
  _id: string;
  name: string;
  zh_name: string;
}

interface PlaceSelectedResult {
  address: string;
  coordinates: [number, number] | null;
  place_id: string;
}

interface RegionData {
  _id: string;
  region_chinese?: string;
  region_english?: string;
  locations?: FlattenedArea[];
}

interface FlattenedArea {
  _id: string;
  location_id: string;
  chinese?: string;
  english?: string;
  location_code?: string;
  region_chinese?: string;
  region_english?: string;
}


interface StaffProfile {
  instagramUsername?: string;
  description?: string;
  zh_description?: string;
  biography?: string;
  subServiceTypes?: (string | SubServiceType)[];
  featuredSkills?: (string | { name?: string; zh_name?: string; title?: string })[];
  businessAddress?: string;
  phone?: string;
  phoneNumber?: string;
  businessArea?: { _id?: string; chinese?: string; english?: string } | string;
  availability?: {
    dayoff?: number[];
    startHour?: string;
    endHour?: string;
  };
}

interface QuickProfileFormData {
  displayName: string;
  description: string;
  zh_description: string;
  businessAddress: string;
  coordinates: number[];
  placeId: string;
  businessArea: string;
  subServiceTypes: (string | SubServiceType)[];
  featuredSkills: string[];
  phoneNumber: string;
  dayOff: string[];
  startHour: string;
  endHour: string;
}

interface QuickProfileStepFormData {
  email?: string;
  businessId?: string;
  stylistId?: string;
  processedStaffId?: string;
  displayName?: string;
  description?: string;
  zh_description?: string;
  businessAddress?: string;
  coordinates?: number[];
  placeId?: string;
  businessArea?: string;
  subServiceTypes?: (string | SubServiceType)[];
  featuredSkills?: string[];
  phoneNumber?: string;
  dayOff?: string[];
  startHour?: string;
  endHour?: string;
  profileComplete?: boolean;
  [key: string]: unknown;
}

interface QuickProfileStepProps {
  formData: QuickProfileStepFormData;
  onUpdate: (data: Record<string, unknown>) => void;
  onBack: () => void;
  onComplete: () => void;
  onError: (error: string) => void;
  isLoading: boolean;
  setIsLoading: (loading: boolean) => void;
  isAnalyzing?: boolean;
}

type LanguageCode = 'en' | 'zh' | 'zh-HK' | 'zh-TW';

interface Benefit {
  icon: React.ReactNode;
  title: string;
  description: string;
}

const QuickProfileStep: React.FC<QuickProfileStepProps> = ({
  formData,
  onUpdate,
  onBack,
  onComplete,
  onError,
  isLoading,
  setIsLoading,
  isAnalyzing
}) => {
  const { t } = useTranslation();
  const [subServiceTypes, setSubServiceTypes] = useState<SubServiceType[]>([]);
  const [isLoadingSubServiceTypes, setIsLoadingSubServiceTypes] = useState<boolean>(true);
  const [subServiceTypesError, setSubServiceTypesError] = useState<string | null>(null);
  const [areas, setAreas] = useState<RegionData[]>([]);
  const [flattenedAreas, setFlattenedAreas] = useState<FlattenedArea[]>([]);
  const [areaSuggestion, setAreaSuggestion] = useState<MatchWithAutoSelectResult | null>(null);
  const [isTranslatingEnToZhDesc, setIsTranslatingEnToZhDesc] = useState<boolean>(false);
  const [isTranslatingZhToEnDesc, setIsTranslatingZhToEnDesc] = useState<boolean>(false);
  const [aiAnalysisData, setAiAnalysisData] = useState<StaffProfile | null>(null);
  const isLoadingAiDataRef = useRef<boolean>(false);

  const { control, handleSubmit, setValue, watch, formState: { errors } } = useForm<QuickProfileFormData>({
    defaultValues: {
      displayName: formData.displayName || '',
      description: formData.description || '',
      zh_description: formData.zh_description || '',
      businessAddress: formData.businessAddress || '',
      coordinates: formData.coordinates || [],
      placeId: formData.placeId || '',
      businessArea: formData.businessArea || '',
      subServiceTypes: [], // Initialize as empty, will be populated after subServiceTypes options are loaded
      featuredSkills: formData.featuredSkills || [],
      phoneNumber: formData.phoneNumber || '',
      dayOff: formData.dayOff || [],
      startHour: formData.startHour || '',
      endHour: formData.endHour || ''
    }
  });

  // Fetch staff profile data from database (includes AI analysis data if available)
  useEffect(() => {
    const fetchStaffProfileData = async (): Promise<void> => {
      // Use processedStaffId if available (from successful AI analysis), otherwise use stylistId
      const staffIdToFetch = formData.processedStaffId || formData.stylistId;

      if (!staffIdToFetch || isLoadingAiDataRef.current) return;

      // Check if we have authentication token
      const token = localStorage.getItem('token');
      if (!token) {
        return;
      }

      isLoadingAiDataRef.current = true;
      try {
        const response = await getStylistDetails(staffIdToFetch);
        const staffProfile = response;

        if (staffProfile) {

                  setAiAnalysisData(staffProfile as StaffProfile);

          // Pre-populate optional fields directly from staffProfile
          if (staffProfile.availability && staffProfile.availability.dayoff && staffProfile.availability.dayoff.length > 0) {
            // Map numeric dayoff values to day names
            // 0 = Sunday, 1 = Monday, 2 = Tuesday, 3 = Wednesday, 4 = Thursday, 5 = Friday, 6 = Saturday
            const dayNames = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
            const dayOffArray = staffProfile.availability.dayoff.map((dayNum: number) => dayNames[dayNum]).filter(Boolean) as string[];
            setValue('dayOff', dayOffArray);
          }

          if (staffProfile.availability?.startHour) {
            setValue('startHour', staffProfile.availability.startHour);
          }

          if (staffProfile.availability?.endHour) {
            setValue('endHour', staffProfile.availability.endHour);
          }
        }
      } catch {
        // Don't show error to user as this is not critical
      } finally {
        isLoadingAiDataRef.current = false;
      }
    };

    fetchStaffProfileData();
  }, [formData.stylistId, formData.processedStaffId, setValue]);

  // Handle AI translation (matches StylistProfileSettings.jsx pattern)
  const handleStreamTranslate = useCallback(async (
    sourceText: string,
    sourceLang: LanguageCode,
    targetLang: LanguageCode,
    targetFieldName: keyof QuickProfileFormData,
    setLoadingState: (loading: boolean) => void
  ): Promise<void> => {
    if (!sourceText) {
      toast.info('Source field for translation is empty.');
      return;
    }
    setLoadingState(true);

    try {
      const formCompatibleSetTargetState = (valueOrUpdater: string | ((prev: string) => string)): void => {
        const currentFieldValue = (watch(targetFieldName) as string) || '';
        if (typeof valueOrUpdater === 'function') {
          setValue(targetFieldName, valueOrUpdater(currentFieldValue) as never, { shouldDirty: true, shouldTouch: true });
        } else {
          setValue(targetFieldName, valueOrUpdater as never, { shouldDirty: true, shouldTouch: true });
        }
      };

      await translateTextStream(
        sourceText,
        targetLang,
        sourceLang,
        formCompatibleSetTargetState,
        () => {
          setLoadingState(false);
          toast.success('Translation streamed successfully!');
        },
        (error: Error) => {
          setLoadingState(false);
          toast.error('Translation failed: ' + (error.message || 'Unknown error'));
        }
      );
    } catch (error: unknown) {
      setLoadingState(false);
      const err = error as Error;
      toast.error('Translation failed: ' + (err.message || 'Unknown error'));
    }
  }, [setValue, watch]);

  // Helper function to determine if a field has data from staffProfile
  const hasStaffProfileData = (fieldName: string): boolean => {
    if (!aiAnalysisData) return false;

    switch (fieldName) {
      case 'displayName':
        return !!aiAnalysisData.instagramUsername;
      case 'description':
        return !!(aiAnalysisData.description || aiAnalysisData.biography);
      case 'zh_description':
        return !!(aiAnalysisData.zh_description || aiAnalysisData.biography);
      case 'subServiceTypes':
        return !!(aiAnalysisData.subServiceTypes && aiAnalysisData.subServiceTypes.length > 0);
      case 'featuredSkills':
        return !!(aiAnalysisData.featuredSkills && aiAnalysisData.featuredSkills.length > 0);
      case 'businessAddress':
        return !!aiAnalysisData.businessAddress;
      case 'phoneNumber':
        return !!(aiAnalysisData.phone || aiAnalysisData.phoneNumber);
      case 'businessArea':
        return !!aiAnalysisData.businessArea;
      default:
        return false;
    }
  };

  // Pre-populate form with staffProfile data when it becomes available
  useEffect(() => {
    if (aiAnalysisData && !isLoadingAiDataRef.current) {

      // Pre-populate display name from Instagram username
      if (aiAnalysisData.instagramUsername && !watch('displayName')) {
        setValue('displayName', aiAnalysisData.instagramUsername);
      }

      // Pre-populate descriptions from database fields
      if (aiAnalysisData.description && !watch('description')) {
        setValue('description', aiAnalysisData.description);
      }

      if (aiAnalysisData.zh_description && !watch('zh_description')) {
        setValue('zh_description', aiAnalysisData.zh_description);
      }

      // Fallback: use biography if descriptions are not available
      if (!aiAnalysisData.description && !aiAnalysisData.zh_description && aiAnalysisData.biography) {
        const bioText = aiAnalysisData.biography;
        // Detect if the text is primarily Chinese
        const isChinese = /[\u4e00-\u9fff]/.test(bioText) &&
          (bioText.match(/[\u4e00-\u9fff]/g) || []).length > (bioText.length * 0.3);

        if (isChinese && !watch('zh_description')) {
          setValue('zh_description', bioText);
        } else if (!isChinese && !watch('description')) {
          setValue('description', bioText);
        }
      }

      // Pre-populate sub-service types from database data
      if (aiAnalysisData.subServiceTypes && aiAnalysisData.subServiceTypes.length > 0 && (!watch('subServiceTypes') || watch('subServiceTypes').length === 0)) {
        setValue('subServiceTypes', aiAnalysisData.subServiceTypes);
      }

      // Pre-populate business address
      if (aiAnalysisData.businessAddress && !watch('businessAddress')) {
        setValue('businessAddress', aiAnalysisData.businessAddress);
      }

      // Pre-populate phone number (normalize to remove spaces)
      const phoneNumber = aiAnalysisData.phone || aiAnalysisData.phoneNumber;
      if (phoneNumber && !watch('phoneNumber')) {
        const normalizedPhone = normalizePhoneNumber(phoneNumber);
        setValue('phoneNumber', normalizedPhone);
      }

      // Pre-populate featured skills
      if (aiAnalysisData.featuredSkills && aiAnalysisData.featuredSkills.length > 0 && (!watch('featuredSkills') || watch('featuredSkills').length === 0)) {
        // Convert objects to strings if necessary
        const skillsAsStrings = aiAnalysisData.featuredSkills.map(skill => {
          if (typeof skill === 'string') {
            return skill;
          } else if (skill && typeof skill === 'object') {
            return skill.name || skill.zh_name || skill.title || String(skill);
          }
          return String(skill);
        }).filter((s): s is string => !!s);
        setValue('featuredSkills', skillsAsStrings);
      }

      // Pre-populate business area
      if (aiAnalysisData.businessArea && !watch('businessArea')) {
        const businessAreaObj = aiAnalysisData.businessArea;
        const businessAreaId = typeof businessAreaObj === 'object' ? businessAreaObj._id : businessAreaObj;
        if (typeof businessAreaId === 'string') {
          setValue('businessArea', businessAreaId);
        } else if (flattenedAreas.length > 0) {
          // Try to match by name if we have the area name but not ID
          const businessAreaName = typeof businessAreaObj === 'object'
            ? (businessAreaObj.chinese || businessAreaObj.english || '')
            : String(businessAreaObj);
          const matchedArea = flattenedAreas.find(area =>
            area.chinese === businessAreaName ||
            area.english === businessAreaName ||
            area.chinese?.includes(businessAreaName) ||
            area.english?.includes(businessAreaName)
          );
          if (matchedArea) {
            setValue('businessArea', matchedArea._id);
          }
        }
      }
    }
  }, [aiAnalysisData, subServiceTypes, areas, flattenedAreas, setValue, watch]);

  // Fetch sub-service types
  useEffect(() => {
    const fetchSubServiceTypes = async (): Promise<void> => {
      setIsLoadingSubServiceTypes(true);
      setSubServiceTypesError(null);

      try {
        const types = await getSubServiceTypes();
        if (types && Array.isArray(types)) {
          setSubServiceTypes(types);

          // Convert formData subServiceTypes IDs to objects if needed
          if (formData.subServiceTypes && formData.subServiceTypes.length > 0) {
            const convertedSubServices = formData.subServiceTypes.map(item => {
              // If item is already an object with name/zh_name, return as is
              if (typeof item === 'object' && (item.name || item.zh_name)) {
                return item;
              }
              // If item is a string ID, find the corresponding object
              const id = typeof item === 'string' ? item : (item as SubServiceType)._id;
              const foundObject = types.find((type: SubServiceType) => type._id === id);
              return foundObject || item; // Return original if not found
            }).filter(Boolean); // Remove any null/undefined items

            setValue('subServiceTypes', convertedSubServices);
          }
        } else {
          setSubServiceTypes([]); // Set empty array as fallback
          setSubServiceTypesError('Invalid data format received');
        }
      } catch (error: unknown) {
        setSubServiceTypes([]); // Set empty array as fallback
        const err = error as Error;
        setSubServiceTypesError(err.message || 'Failed to load sub-service types');
      } finally {
        setIsLoadingSubServiceTypes(false);
      }
    };
    fetchSubServiceTypes();
  }, [formData.subServiceTypes, setValue]);

  // Fetch areas
  useEffect(() => {
    const fetchAreas = async (): Promise<void> => {
      try {
        const areaResponse = await getAreas();
        const areaData = areaResponse;
        setAreas(areaData as RegionData[]);

        // Flatten areas to create location list for dropdown
        const flattened: FlattenedArea[] = [];
        areaData.forEach((region) => {
          if (region.locations && Array.isArray(region.locations)) {
            region.locations.forEach((location) => {
              flattened.push({
                _id: location._id,
                location_id: location._id, // For compatibility with area matching
                chinese: location.chinese,
                english: location.english,
                location_code: location.location_code,
                region_chinese: region.region_chinese,
                region_english: region.region_english
              });
            });
          }
        });
        setFlattenedAreas(flattened);

      } catch (error: unknown) {
        console.error('Error fetching areas:', error);
      }
    };
    fetchAreas();
  }, []);


  const handlePlaceSelected = (placeData: PlaceSelectedResult): void => {
    // Set coordinates in GeoJSON format [lng, lat] to match area matching expectations
    setValue('coordinates', placeData.coordinates || [0, 0]);
    setValue('placeId', placeData.place_id || '');
    // Note: businessAddress is already set by field.onChange in onPlaceSelected callback

    // Perform area matching detection
    if (areas.length > 0) {
      const areaMatchResult = matchPlaceToAreaWithAutoSelect(
        { address: placeData.address } as AreaUtilPlaceData,
        areas as AreaUtilRegionData[]
      );
      console.log('[QuickProfileStep] Area match result:', areaMatchResult);

      if (areaMatchResult.shouldAutoSelect && areaMatchResult.match) {
        const { match } = areaMatchResult;
        setValue('businessArea', match.location_id, { shouldValidate: true });
        toast.success(t('areaSuggestion.autoSelected', `Area auto-selected: ${match.chinese || match.english} (${areaMatchResult.confidence}% confidence)`));
      } else if (areaMatchResult.shouldShowSuggestion) {
        setAreaSuggestion(areaMatchResult);
      }
    }
  };

  // Handle area suggestion acceptance
  const handleAreaSuggestionAccept = (payload: { regionId: string; locationId: string; match: { chinese?: string; english?: string } }): void => {
    console.log('[QuickProfileStep] Accepting area suggestion:', payload);
    setValue('businessArea', payload.locationId, { shouldValidate: true });
    setAreaSuggestion(null);
    toast.success(t('areaSuggestion.accepted', `Area suggestion accepted: ${payload.match.chinese || payload.match.english}`));
  };

  // Handle area suggestion dismissal
  const handleAreaSuggestionDismiss = (): void => {
    setAreaSuggestion(null);
  };

  // Handle form submission
  const onSubmit = async (data: QuickProfileFormData): Promise<void> => {
    setIsLoading(true);
    onError('');

    try {
      // Format phone number: if user entered 8 digits, add +852 prefix
      if (data.phoneNumber && /^\d{8}$/.test(data.phoneNumber)) {
        data.phoneNumber = `+852${data.phoneNumber}`;
      }

      // Prepare business profile data
      const businessProfileData: Record<string, unknown> = {
        businessAddress: data.businessAddress,
        coordinates: data.coordinates,
        placeId: data.placeId,
        businessArea: data.businessArea
      };

      // Prepare stylist profile data
      const stylistData: Record<string, unknown> = {
        displayName: data.displayName,
        description: data.description,
        zh_description: data.zh_description,
        subServiceTypes: data.subServiceTypes,
        featuredSkills: data.featuredSkills || [],
        phoneNumber: data.phoneNumber ? formatPhoneForStorage(data.phoneNumber) : null,
        // Only include optional fields if they have values
        ...(data.dayOff && data.dayOff.length > 0 && { dayOff: data.dayOff }),
        ...(data.startHour && { startHour: data.startHour }),
        ...(data.endHour && { endHour: data.endHour }),
        // Include any existing stylist data that should be preserved
        ...(formData.stylistId && { _id: formData.stylistId })
        // AI analysis data is now stored in database and not sent here
      };

      // Update stylist and business profiles

      // Update business profile
      await updateBusinessProfile(businessProfileData as Partial<{ businessAddress: string; coordinates: [number, number]; placeId: string; businessArea: string }>);

      // Update stylist details
      await updateStylistDetails(stylistData as Record<string, FormDataValue>);

      // Update localStorage user information to ensure proper session
      const existingUser = JSON.parse(localStorage.getItem('user') || '{}');
      const updatedUser = {
        ...existingUser,
        _id: formData.stylistId, // Set the staff ID as the user ID
        role: 'business', // Ensure role is set correctly
        displayName: data.displayName,
        email: formData.email,
        businessId: formData.businessId,
        status: 'pending' // Keep as pending until email verification
      };
      localStorage.setItem('user', JSON.stringify(updatedUser));

      // Update form data with completion status
      onUpdate({
        ...data,
        profileComplete: true
      });

      toast.success(t('quickOnboarding.profile.success', 'Profile updated successfully!'));
      onComplete();

    } catch (error: unknown) {
      console.error('Error updating profile:', error);
      const err = error as Error;
      onError(err.message || 'Failed to update profile');
    } finally {
      setIsLoading(false);
    }
  };

  const benefits: Benefit[] = [
    {
      icon: <Build color="primary" />,
      title: t('quickOnboarding.profile.benefit1', 'Quick Setup'),
      description: t('quickOnboarding.profile.benefit1Desc', 'Get listed in minutes, not hours')
    },
    {
      icon: <Star color="primary" />,
      title: t('quickOnboarding.profile.benefit2', 'Professional Profile'),
      description: t('quickOnboarding.profile.benefit2Desc', 'Stand out with a complete profile')
    },
    {
      icon: <Schedule color="primary" />,
      title: t('quickOnboarding.profile.benefit3', 'Start Earning'),
      description: t('quickOnboarding.profile.benefit3Desc', 'Begin receiving bookings immediately')
    }
  ];

  return (
    <Box className="profile-step">
      <Typography variant="h5" gutterBottom>
        {t('quickOnboarding.profile.title', 'Complete Your Profile')}
      </Typography>

      <Typography variant="body1" color="text.secondary" paragraph>
        {t('quickOnboarding.profile.description', 'Just a few more details and you\'ll be ready to start receiving bookings')}
      </Typography>

      <Divider sx={{ my: 3 }} />

      {/* Benefits Preview - only show when not completed */}
      {!formData.profileComplete && (
        <Card elevation={1} className="benefits-card" sx={{ mb: 3 }}>
          <CardContent>
            <Typography variant="h6" color="primary" className="benefits-title" gutterBottom>
              {t('quickOnboarding.profile.almostThere', 'You\'re almost there!')}
            </Typography>
            <List dense>
              {benefits.map((benefit, index) => (
                <ListItem key={index} className="benefit-item" sx={{ px: 0 }}>
                  <ListItemIcon sx={{ minWidth: 40 }}>
                    {benefit.icon}
                  </ListItemIcon>
                  <ListItemText
                    primary={benefit.title}
                    secondary={benefit.description}
                  />
                </ListItem>
              ))}
            </List>
          </CardContent>
        </Card>
      )}

      {/* AI Analysis Status */}
      {isAnalyzing && (
        <Alert severity="info" sx={{ mb: 3 }} icon={<CircularProgress size={20} />}>
          <Typography variant="body2">
            {t('quickOnboarding.profile.aiAnalyzing', '🤖 Analyzing your Instagram profile to pre-fill form fields...')}
          </Typography>
        </Alert>
      )}

      {aiAnalysisData && (
        <Alert severity="success" sx={{ mb: 3 }} icon={<AutoFixHighIcon />}>
          <Typography variant="body2">
            {t('quickOnboarding.profile.aiComplete', '✨ Form fields pre-filled from your Instagram profile! You can edit any field as needed.')}
          </Typography>
        </Alert>
      )}

      <form onSubmit={handleSubmit(onSubmit)} className="profile-form">
          <Stack spacing={3} className="form-stack">
            {/* Display Name */}
            <Controller
              name="displayName"
              control={control}
              rules={{
                required: t('quickOnboarding.profile.nameRequired', 'Display name is required'),
                minLength: {
                  value: 2,
                  message: t('quickOnboarding.profile.nameMinLength', 'Name must be at least 2 characters')
                }
              }}
              render={({ field }) => (
                <Box>
                  <TextField
                    {...field}
                    fullWidth
                    label={t('quickOnboarding.profile.displayName', 'Display Name')}
                    placeholder={t('quickOnboarding.profile.namePlaceholder', 'How clients will see you')}
                    error={!!errors.displayName}
                    helperText={errors.displayName?.message}
                    InputProps={{
                      startAdornment: <Person color="action" sx={{ mr: 1 }} />,
                      endAdornment: hasStaffProfileData('displayName') ? (
                        <AutoFixHighIcon
                          color="primary"
                          sx={{ fontSize: 20 }}
                          titleAccess={t('quickOnboarding.profile.aiPopulated', 'AI suggested')}
                        />
                      ) : undefined
                    }}
                  />
                  {hasStaffProfileData('displayName') && (
                    <Typography variant="caption" color="primary" sx={{ display: 'flex', alignItems: 'center', mt: 0.5 }}>
                      <AutoFixHighIcon sx={{ fontSize: 14, mr: 0.5 }} />
                      {t('quickOnboarding.profile.aiSuggested', 'AI suggested from your Instagram profile')}
                    </Typography>
                  )}
                </Box>
              )}
            />

            {/* Sub-Service Types */}
            <Controller
              name="subServiceTypes"
              control={control}
              rules={{
                validate: value => value && value.length > 0 || t('quickOnboarding.profile.subServiceTypesRequired', 'Please select at least one sub-service type')
              }}
              render={({ field }) => (
                <FormControl fullWidth error={!!errors.subServiceTypes}>
                  <Autocomplete
                    multiple
                    disabled={isLoadingSubServiceTypes || !!subServiceTypesError}
                    loading={isLoadingSubServiceTypes}
                    options={subServiceTypes}
                    value={(() => {
                      const currentValue = (field.value || []) as (string | SubServiceType)[];

                      // Convert any ID strings to objects in real-time
                      const convertedValue = currentValue.map(item => {
                        if (typeof item === 'string') {
                          const foundObject = subServiceTypes.find(type => type._id === item);
                          return foundObject || item;
                        }
                        return item;
                      });

                      return convertedValue as SubServiceType[];
                    })()}
                    onChange={(_, newValue) => field.onChange(newValue)}
                    getOptionLabel={(option) => {
                      if (typeof option === 'string') return option;
                      return `${option.name || 'Unknown'} (${option.zh_name || 'Unknown'})`;
                    }}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label={t('quickOnboarding.profile.subServiceTypes', 'Sub-Service Types') + ' *'}
                        placeholder={field.value && field.value.length > 0 ? '' : t('quickOnboarding.profile.selectSubServiceTypes', 'Select your sub-service types')}
                        error={!!errors.subServiceTypes}
                        helperText={
                          errors.subServiceTypes?.message ||
                          (isLoadingSubServiceTypes ? 'Loading service types...' : '') ||
                          (subServiceTypesError ? `Error: ${subServiceTypesError}` : '') ||
                          (subServiceTypes.length === 0 ? 'No service types available' : '')
                        }
                        InputProps={{
                          ...params.InputProps,
                          startAdornment: (
                            <>
                              <Build color="action" sx={{ mr: 1 }} />
                              {params.InputProps.startAdornment}
                            </>
                          ),
                          endAdornment: (
                            <>
                              {hasStaffProfileData('subServiceTypes') ? (
                                <AutoFixHighIcon
                                  color="primary"
                                  sx={{ fontSize: 20, mr: 1 }}
                                  titleAccess={t('quickOnboarding.profile.aiPopulated', 'AI suggested')}
                                />
                              ) : null}
                              {params.InputProps.endAdornment}
                            </>
                          )
                        } as React.ComponentProps<typeof TextField>['InputProps']}
                      />
                    )}
                    isOptionEqualToValue={(option, value) => {
                      if (typeof option === 'string' || typeof value === 'string') return option === value;
                      return option._id === value._id;
                    }}
                  />
                  {hasStaffProfileData('subServiceTypes') && (
                    <Typography variant="caption" color="primary" sx={{ display: 'flex', alignItems: 'center', mt: 0.5 }}>
                      <AutoFixHighIcon sx={{ fontSize: 14, mr: 0.5 }} />
                      {t('quickOnboarding.profile.aiSuggested', 'AI suggested from your Instagram profile')}
                    </Typography>
                  )}
                </FormControl>
              )}
            />

            {/* Featured Skills */}
            <Controller
              name="featuredSkills"
              control={control}
              render={({ field }) => (
                <Box>
                  <Autocomplete
                    multiple
                    freeSolo
                    options={[] as string[]}
                    value={field.value || []}
                    onChange={(_, newValue) => {
                      // Ensure all values are strings
                      const stringValues = newValue.map(value => {
                        if (typeof value === 'string') {
                          return value;
                        } else if (value && typeof value === 'object') {
                          const obj = value as { name?: string; zh_name?: string; title?: string };
                          return obj.name || obj.zh_name || obj.title || String(value);
                        }
                        return String(value);
                      });
                      field.onChange(stringValues);
                    }}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label={t('quickOnboarding.profile.featuredSkills', 'Featured Skills')}
                        placeholder={field.value && field.value.length > 0 ? '' : t('quickOnboarding.profile.addSkills', 'Add your key skills and specialties')}
                        InputProps={{
                          ...params.InputProps,
                          startAdornment: (
                            <>
                              <Star color="action" sx={{ mr: 1 }} />
                              {params.InputProps.startAdornment}
                            </>
                          ),
                          endAdornment: (
                            <>
                              {hasStaffProfileData('featuredSkills') ? (
                                <AutoFixHighIcon
                                  color="primary"
                                  sx={{ fontSize: 20, mr: 1 }}
                                  titleAccess={t('quickOnboarding.profile.aiPopulated', 'AI suggested')}
                                />
                              ) : null}
                              {params.InputProps.endAdornment}
                            </>
                          )
                        } as React.ComponentProps<typeof TextField>['InputProps']}
                      />
                    )}
                  />
                  {hasStaffProfileData('featuredSkills') && (
                    <Typography variant="caption" color="primary" sx={{ display: 'flex', alignItems: 'center', mt: 0.5 }}>
                      <AutoFixHighIcon sx={{ fontSize: 14, mr: 0.5 }} />
                      {t('quickOnboarding.profile.aiSuggested', 'AI suggested from your Instagram profile')}
                    </Typography>
                  )}
                  <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
                    {t('quickOnboarding.profile.skillsHelp', 'Type skills and press Enter to add them')}
                  </Typography>
                </Box>
              )}
            />

            {/* Phone Number */}
            <Controller
              name="phoneNumber"
              control={control}
              rules={{
                validate: value => {
                  if (!value) return true; // Phone is optional
                  return validateHKPhoneNumber(value) || t('quickOnboarding.profile.phoneInvalid', 'Please enter a valid Hong Kong phone number (+852XXXXXXXX)');
                }
              }}
              render={({ field }) => (
                <Box>
                  <TextField
                    {...field}
                    fullWidth
                    label={t('quickOnboarding.profile.phoneNumber', 'Phone Number (Optional)')}
                    placeholder="67396213 or +85267396213"
                    error={!!errors.phoneNumber}
                    helperText={errors.phoneNumber?.message || t('quickOnboarding.profile.phoneHelp', 'Verified phone numbers receive 3x more bookings')}
                    InputProps={{
                      startAdornment: <Phone color="action" sx={{ mr: 1 }} />,
                      endAdornment: hasStaffProfileData('phoneNumber') ? (
                        <AutoFixHighIcon
                          color="primary"
                          sx={{ fontSize: 20 }}
                          titleAccess={t('quickOnboarding.profile.aiPopulated', 'AI suggested')}
                        />
                      ) : undefined
                    }}
                  />
                  {hasStaffProfileData('phoneNumber') && (
                    <Typography variant="caption" color="primary" sx={{ display: 'flex', alignItems: 'center', mt: 0.5 }}>
                      <AutoFixHighIcon sx={{ fontSize: 14, mr: 0.5 }} />
                      {t('quickOnboarding.profile.aiSuggested', 'AI suggested from your Instagram profile')}
                    </Typography>
                  )}
                </Box>
              )}
            />

            {/* Day Off - Optional field */}
            <Controller
              name="dayOff"
              control={control}
              render={({ field }) => (
                <FormControl fullWidth>
                  <InputLabel>{t('quickOnboarding.profile.dayOff', 'Day Off')} ({t('common.optional', 'Optional')})</InputLabel>
                  <Select
                    {...field}
                    multiple
                    value={field.value || []}
                    label={`${t('quickOnboarding.profile.dayOff', 'Day Off')} (${t('common.optional', 'Optional')})`}
                    renderValue={(selected) => {
                      // Ensure selected is always an array
                      const selectedArray = Array.isArray(selected) ? selected : [];
                      if (selectedArray.length === 0) {
                        return t('common.none', 'None');
                      }
                      return selectedArray.map((day: string) => t(`common.${day.toLowerCase()}`, day)).join(', ');
                    }}
                  >
                    <MenuItem value="Monday">{t('common.monday', 'Monday')}</MenuItem>
                    <MenuItem value="Tuesday">{t('common.tuesday', 'Tuesday')}</MenuItem>
                    <MenuItem value="Wednesday">{t('common.wednesday', 'Wednesday')}</MenuItem>
                    <MenuItem value="Thursday">{t('common.thursday', 'Thursday')}</MenuItem>
                    <MenuItem value="Friday">{t('common.friday', 'Friday')}</MenuItem>
                    <MenuItem value="Saturday">{t('common.saturday', 'Saturday')}</MenuItem>
                    <MenuItem value="Sunday">{t('common.sunday', 'Sunday')}</MenuItem>
                  </Select>
                </FormControl>
              )}
            />

            {/* Start Hour - Optional field */}
            <Controller
              name="startHour"
              control={control}
              render={({ field }) => (
                <TextField
                  {...field}
                  fullWidth
                  type="time"
                  label={`${t('quickOnboarding.profile.startHour', 'Start Hour')} (${t('common.optional', 'Optional')})`}
                  helperText={t('quickOnboarding.profile.startHourHelp', 'When you typically start work')}
                  InputLabelProps={{
                    shrink: true,
                  }}
                />
              )}
            />

            {/* End Hour - Optional field */}
            <Controller
              name="endHour"
              control={control}
              render={({ field }) => (
                <TextField
                  {...field}
                  fullWidth
                  type="time"
                  label={`${t('quickOnboarding.profile.endHour', 'End Hour')} (${t('common.optional', 'Optional')})`}
                  helperText={t('quickOnboarding.profile.endHourHelp', 'When you typically finish work')}
                  InputLabelProps={{
                    shrink: true,
                  }}
                />
              )}
            />

            {/* Business Address */}
            <Controller
              name="businessAddress"
              control={control}
              rules={{
                required: t('quickOnboarding.profile.addressRequired', 'Business address is required')
              }}
              render={({ field }) => (
                <Box>
                  <AddressAutocomplete
                    label={t('quickOnboarding.profile.businessAddress', 'Business Address')}
                    value={field.value}
                    onInputChange={(value: string) => field.onChange(value)}
                    onPlaceSelected={(placeData: PlaceSelectedResult) => {
                      console.log('[QuickProfileStep] onPlaceSelected called with:', placeData);
                      // Update the field value directly to prevent clearing
                      field.onChange(placeData.address);
                      // Call the handler for additional logic (area detection, etc.)
                      handlePlaceSelected(placeData);
                    }}
                    error={!!errors.businessAddress}
                    helperText={errors.businessAddress?.message}
                  />
                  {hasStaffProfileData('businessAddress') && (
                    <Typography variant="caption" color="primary" sx={{ display: 'flex', alignItems: 'center', mt: 0.5 }}>
                      <AutoFixHighIcon sx={{ fontSize: 14, mr: 0.5 }} />
                      {t('quickOnboarding.profile.aiSuggested', 'AI suggested from your Instagram profile')}
                    </Typography>
                  )}
                  {(
                    <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
                      {t('quickOnboarding.profile.addressHelp', 'This will be visible to clients when they book')}
                    </Typography>
                  )}
                  {/* Area Suggestion */}
                  {areaSuggestion && (
                    <AreaSuggestion
                      matchResult={areaSuggestion as AreaSuggestionMatchResult}
                      onAcceptSuggestion={handleAreaSuggestionAccept as (payload: AcceptSuggestionPayload) => void}
                      onDismiss={handleAreaSuggestionDismiss}
                    />
                  )}
                </Box>
              )}
            />

            {/* Business Area */}
            <Controller
              name="businessArea"
              control={control}
              rules={{
                required: t('quickOnboarding.profile.areaRequired', 'Please select a business area')
              }}
              render={({ field }) => (
                <Box>
                  <FormControl fullWidth error={!!errors.businessArea}>
                    <InputLabel>{t('quickOnboarding.profile.businessArea', 'Business Area')}</InputLabel>
                    <Select
                      {...field}
                      label={t('quickOnboarding.profile.businessArea', 'Business Area')}
                    >
                      {flattenedAreas.map((area) => (
                        <MenuItem key={area._id} value={area._id}>
                          {area.chinese || 'Unknown'} ({area.english || 'Unknown'})
                        </MenuItem>
                      ))}
                    </Select>
                    {errors.businessArea && (
                      <FormHelperText>{errors.businessArea.message}</FormHelperText>
                    )}
                  </FormControl>
                  {hasStaffProfileData('businessArea') && (
                    <Typography variant="caption" color="primary" sx={{ display: 'flex', alignItems: 'center', mt: 0.5 }}>
                      <AutoFixHighIcon sx={{ fontSize: 14, mr: 0.5 }} />
                      {t('quickOnboarding.profile.aiSuggested', 'AI suggested from your Instagram profile')}
                    </Typography>
                  )}
                </Box>
              )}
            />

            {/* English Description with Translation Button */}
            <Box sx={{ mt: 2, mb: 0.5, display: 'flex', justifyContent: 'flex-start' }}>
              <Button
                onClick={() => {
                  const currentDesc = watch('description');
                  handleStreamTranslate(currentDesc, 'en', 'zh-HK', 'zh_description', setIsTranslatingEnToZhDesc);
                }}
                disabled={isTranslatingEnToZhDesc || isTranslatingZhToEnDesc || isLoading || !watch('description')}
                variant="outlined"
                size="small"
                className="translation-button"
                startIcon={isTranslatingEnToZhDesc ? <CircularProgress size={16} /> : <AutoFixHighIcon />}
              >
                {t('quickListingWizard.translateWithAI')} {t('common.toChinese')}
              </Button>
            </Box>
            <Controller
              name="description"
              control={control}
              rules={{
                required: t('quickOnboarding.profile.descriptionRequired', 'Please add a description'),
                maxLength: {
                  value: 500,
                  message: t('quickOnboarding.profile.descriptionMaxLength', 'Description must be less than 500 characters')
                }
              }}
              render={({ field }) => (
                <TextField
                  {...field}
                  fullWidth
                  multiline
                  rows={3}
                  label={t('quickOnboarding.profile.description', 'Tell clients about yourself (English)')}
                  placeholder={t('quickOnboarding.profile.descriptionPlaceholder', 'Describe your experience, specialties, and what makes you unique...')}
                  error={!!errors.description}
                  helperText={errors.description?.message || `${field.value.length}/500`}
                  disabled={isTranslatingEnToZhDesc || isTranslatingZhToEnDesc}
                  InputProps={{
                    startAdornment: <Description color="action" sx={{ mr: 1, alignSelf: 'flex-start', mt: 1 }} />
                  }}
                />
              )}
            />

            {/* Chinese Description with Translation Button */}
            <Box sx={{ mt: 1, mb: 0.5, display: 'flex', justifyContent: 'flex-start' }}>
              <Button
                onClick={() => {
                  const currentZhDesc = watch('zh_description');
                  handleStreamTranslate(currentZhDesc, 'zh-HK', 'en', 'description', setIsTranslatingZhToEnDesc);
                }}
                disabled={isTranslatingEnToZhDesc || isTranslatingZhToEnDesc || isLoading || !watch('zh_description')}
                variant="outlined"
                size="small"
                className="translation-button"
                startIcon={isTranslatingZhToEnDesc ? <CircularProgress size={16} /> : <AutoFixHighIcon />}
              >
                {t('quickListingWizard.translateWithAI')} {t('common.toEnglish')}
              </Button>
            </Box>
            <Controller
              name="zh_description"
              control={control}
              rules={{
                required: t('quickOnboarding.profile.descriptionRequired', 'Please add a Chinese description'),
                maxLength: {
                  value: 500,
                  message: t('quickOnboarding.profile.descriptionMaxLength', 'Description must be less than 500 characters')
                }
              }}
              render={({ field }) => (
                <TextField
                  {...field}
                  fullWidth
                  multiline
                  rows={3}
                  label={t('quickOnboarding.profile.zh_description', 'Tell clients about yourself (Chinese)')}
                  placeholder={t('quickOnboarding.profile.descriptionPlaceholder', 'Describe your experience, specialties, and what makes you unique...')}
                  error={!!errors.zh_description}
                  helperText={errors.zh_description?.message || `${field.value.length}/500`}
                  disabled={isTranslatingEnToZhDesc || isTranslatingZhToEnDesc}
                  InputProps={{
                    startAdornment: <Description color="action" sx={{ mr: 1, alignSelf: 'flex-start', mt: 1 }} />
                  }}
                />
              )}
            />
          </Stack>

          {/* Navigation buttons */}
          <Box className="profile-navigation" sx={{ display: 'flex', justifyContent: 'space-between', mt: 4 }}>
            <Button
              variant="outlined"
              onClick={onBack}
              disabled={isLoading}
              className="back-button"
            >
              {t('quickOnboarding.profile.back', 'Back to Showcases')}
            </Button>

            <Button
              type="submit"
              variant="contained"
              size="large"
              disabled={isLoading}
              className="submit-button"
              sx={{ px: 4 }}
            >
              {isLoading
                ? t('quickOnboarding.profile.updating', 'Updating Profile...')
                : t('quickOnboarding.profile.update', 'Update Profile')
              }
            </Button>
          </Box>
        </form>
    </Box>
  );
};

export default QuickProfileStep;
